import format from 'date-fns/format';
import constants from '@/shared/config/constants';
import addMonths from 'date-fns/addMonths';
import generateSrc from '@/core/responsive/image/helpers/generate-src';
import { differenceInDays, endOfToday } from 'date-fns';
import { MediaModel } from '@/types/B2CcontentServerContract';

export default {
    toggleCollectionCap,
    formatDate,
    daysBetweenDates,
    getBasketQuantityFromLineItems,
    daysFromNow,
    delay,
    getAirbnbPickerDate,
    unwrapModel,
    formatString,
    ifMediaModelIsVideoReturnIdFromVimeoUrl,
    copyTextToClipboard,
    downloadBlob,
    getProductImageUrlFromSku,
    stripEmojis,
};

function toggleCollectionCap(list: any[] = [], threshold: number, expand: boolean) {
    const items = list ? list.slice() : [];
    if (expand || (items && items.length <= threshold)) {
        return items;
    } else {
        items.length = threshold;
        return items;
    }
}

function getBasketQuantityFromLineItems(lineItems): number {
    let quantity = 0;
    if (lineItems) {
        lineItems.forEach(lineItem => quantity += lineItem.quantity);
    }
    return quantity;
}

function formatDate(date: string | Date): string {
    return format(new Date(date), constants.dateFormat);
}

function daysBetweenDates(dateLeft: string | Date, dateRight: string | Date): number {
    const firstDate = new Date(dateLeft);
    const secondDate = new Date(dateRight);
    return differenceInDays(firstDate, secondDate);
}

function daysFromNow(date: string | Date): number {
    const dateLeft = new Date(date);
    const dateRight = endOfToday();
    return differenceInDays(dateLeft, dateRight);
}

function getAirbnbPickerDate(monthsOffset: number = 0): string {
    const today = new Date(Date.now());
    return format(addMonths(today, monthsOffset), constants.airbnbPickerDateFormat);
}

function delay(timing): Promise<any> {
    return new Promise((resolve, reject) => {
        setTimeout(resolve, timing);
    });
}

function unwrapModel<T>(res: { model: T, messages: any}): T {
    return res?.model;
}

function formatString(string: string = '', args: string | string[] = []): string {
    if (typeof args === 'string') {
        args = [args];
    }

    return args.reduce((result, arg, ix) => {
        return result.split(`{${ix}}`).join(arg);
    }, string);
}

function ifMediaModelIsVideoReturnIdFromVimeoUrl(mediaModel: MediaModel): number | undefined {
    if (isVimeoMediaType(mediaModel)) {
        return mediaModel.model.id || getIdFromVimeoUrl(mediaModel.model.url);
    }
    return undefined;
}

function isVimeoMediaType(mediaModel: any): boolean {
    return !!(mediaModel && mediaModel.type === constants.media.vimeoModel);
}

function getIdFromVimeoUrl(url: string) {
    const regexForFirstNumber = /\d+/;
    const matchFound = url.match(regexForFirstNumber);
    return matchFound && !!matchFound[0] ? matchFound[0] : undefined;
}

function getProductImageUrlFromSku(sku: string, width?: number): string {
    const url = `/products/${sku}.webp`;
    return generateSrc(url, { width, isProductImage: true });
}

function copyTextToClipboard(text: string): Promise<any> {
    return new Promise((resolve, reject) => {
        if (!navigator.clipboard) {
            fallbackCopyTextToClipboard(text).then(resolve, reject);
        }
        navigator.clipboard.writeText(text).then(resolve, reject);
    });
}

function downloadBlob(blob: BlobPart, fileName: string, type: string = 'application/pdf'): void {
    const blobUrl = window.URL.createObjectURL(new Blob([blob], { type }));

    // Create hidden element
    let link: HTMLAnchorElement | null = document.createElement('a');
    link.classList.add('hidden');
    const linkTag = document.body.appendChild(link);

    // Set download info and blank target as fallback (download is quirky on iOS)
    link.href = blobUrl;
    link.target = '_blank';
    link.download = fileName;
    link.click();

    // Cleanup to avoid memleak
    document.body.removeChild(linkTag);
    link = null;
    window.URL.revokeObjectURL(blobUrl);
}

function fallbackCopyTextToClipboard(text): Promise<any> {
    return new Promise((resolve, reject) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;

        // Avoid scrolling to bottom
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                resolve(successful);
            } else {
                reject(successful);
            }
        } catch (err) {
            reject(err);
        }
        document.body.removeChild(textArea);
    });
}

function stripEmojis(text: string, regex: string = '\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]'): string {
    return text?.replace(new RegExp(regex, 'g'), '') ?? '';
}
